// Σ Formula builder — generates Liquid from dropdowns so users never have to
// write expressions by hand. Two modes: a FIELD value (per record — for Data
// Table template rows and batch text) and a SUMMARY over the whole dataset
// (count/sum/avg/min/max, optional row filter — for summary tables). Shows the
// generated expression AND its evaluated result live against the imported data;
// the expression stays editable afterwards (the builder writes plain Liquid).
import { el, openModal, toast } from './../ui/dom'
import { state } from './../state'
import { lq, lqError, rowScope } from './../liquid'

type Insert = (tpl: string) => boolean

export interface Formula {
  mode: 'field' | 'summary'
  field: string
  agg: 'count' | 'sum' | 'avg' | 'min' | 'max'
  filterOn: boolean
  filterCol: string
  filterVal: string
  div100: boolean
  format: 'none' | 'number' | 'currency' | 'percent' | 'date'
  decimals: number
  currency: string
  locale: string
  dateFmt: string
}

/** Pure generator — the single source of truth for what the builder emits. */
export function buildFormula(f: Formula): string {
  let expr: string
  if (f.mode === 'summary') {
    expr = 'rows'
    if (f.filterOn && f.filterCol) {
      const v = f.filterVal.trim()
      expr += ` | where: "${f.filterCol}", ${/^-?\d+(\.\d+)?$/.test(v) ? v : `"${v}"`}`
    }
    expr += f.agg === 'count' ? ' | count' : ` | ${f.agg}: "${f.field}"`
  } else {
    expr = f.field
  }
  if (f.div100) expr += ' | divided_by: 100.0'
  if (f.format === 'number') expr += ` | number: ${f.decimals}`
  else if (f.format === 'currency') expr += ` | money: "${f.currency}", "${f.locale}"`
  else if (f.format === 'percent') expr += ` | percent: ${f.decimals}`
  else if (f.format === 'date') expr += ` | date: "${f.dateFmt}"`
  return `{{ ${expr} }}`
}

function defaultCurrency(locale: string): string {
  if (/^(nl|de|fr|es|it|pt|fi|at|be|el)/i.test(locale)) return 'EUR'
  if (/^en-GB/i.test(locale)) return 'GBP'
  return 'USD'
}

const DATE_FORMATS: [string, string][] = [
  ['%d %b %Y', '01 Jun 2026'],
  ['%d-%m-%Y', '01-06-2026'],
  ['%b %d, %Y', 'Jun 01, 2026'],
  ['%d %b %Y, %H:%M', '01 Jun 2026, 21:37'],
  ['%H:%M', '21:37'],
]

export function openFormulaBuilder(insert?: Insert, initialMode: Formula['mode'] = 'field'): void {
  const headers = state.batchHeaders ?? []
  const rows = state.batchData ?? []
  const locale = navigator.language || 'en-US'
  const isNumeric = (h: string) => { const v = rows[0]?.[h]; return v !== '' && v != null && !Number.isNaN(Number(v)) }
  const firstNumeric = headers.find(isNumeric) ?? headers[0] ?? 'amount'

  const f: Formula = {
    mode: initialMode,
    field: initialMode === 'summary' ? firstNumeric : (headers[0] ?? 'name'),
    agg: initialMode === 'summary' ? 'sum' : 'count',
    filterOn: false,
    filterCol: headers[0] ?? '',
    filterVal: '',
    div100: false,
    format: 'none',
    decimals: 2,
    currency: defaultCurrency(locale),
    locale,
    dateFmt: DATE_FORMATS[0][0],
  }

  // ---- tiny local form helpers ----
  const row = (label: string, control: HTMLElement): HTMLElement => {
    const r = el('label', 'flex items-center gap-2')
    const l = el('span', 'text-[11px] text-slate-400 shrink-0', label); l.style.minWidth = '90px'
    r.append(l, control); return r
  }
  const select = (opts: [string, string][], value: string, onChange: (v: string) => void): HTMLSelectElement => {
    const s = el('select', 'prop-input flex-1') as HTMLSelectElement
    for (const [v, lbl] of opts) { const o = el('option'); o.value = v; o.textContent = lbl; if (v === value) o.selected = true; s.append(o) }
    s.addEventListener('change', () => onChange(s.value))
    return s
  }
  const fieldControl = (value: string, onChange: (v: string) => void): HTMLElement => {
    if (headers.length) return select(headers.map((h) => [h, h]), value, onChange)
    const i = el('input', 'prop-input flex-1') as HTMLInputElement
    i.type = 'text'; i.value = value; i.placeholder = 'field name'
    i.addEventListener('input', () => onChange(i.value))
    return i
  }
  const checkbox = (checked: boolean, onChange: (v: boolean) => void): HTMLInputElement => {
    const c = el('input') as HTMLInputElement
    c.type = 'checkbox'; c.checked = checked; c.style.accentColor = '#3b82f6'
    c.addEventListener('change', () => onChange(c.checked))
    return c
  }

  const form = el('div', 'flex flex-col gap-2')
  const preview = el('div', 'rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 mt-1')
  const codeEl = el('code', 'block text-[11px] text-blue-400 break-all')
  const resultEl = el('div', 'text-[12px] mt-1')
  preview.append(codeEl, resultEl)

  const updatePreview = (): void => {
    const code = buildFormula(f)
    codeEl.textContent = code
    if (!rows.length) {
      resultEl.className = 'text-[11px] mt-1 text-slate-500'
      resultEl.textContent = 'Import data to see the computed result here.'
      return
    }
    const scope = f.mode === 'summary' ? { rows } : rowScope(rows[0])
    const err = lqError(code, scope)
    if (err) {
      resultEl.className = 'text-[11px] mt-1 text-red-400'
      resultEl.textContent = `⚠ ${err.split('\n')[0]}`
    } else {
      resultEl.className = 'text-[12px] mt-1 text-emerald-400 font-medium'
      resultEl.textContent = `= ${lq(code, scope)}${f.mode === 'field' ? '   (first row)' : ''}`
    }
  }

  const render = (): void => {
    form.replaceChildren()

    // Mode toggle
    const modes = el('div', 'flex gap-2')
    for (const [m, lbl] of [['field', 'Field value'], ['summary', 'Summary of all rows']] as const) {
      const b = el('button', `tb-btn text-xs ${f.mode === m ? 'primary' : ''}`, lbl) as HTMLButtonElement
      b.type = 'button'
      if (f.mode !== m) b.style.cssText = 'background:#1e293b;border:1px solid #334155'
      b.addEventListener('click', () => {
        f.mode = m
        if (m === 'summary' && !isNumeric(f.field)) f.field = firstNumeric
        render()
      })
      modes.append(b)
    }
    form.append(row('Type', modes))

    if (f.mode === 'summary') {
      form.append(row('Calculate', select(
        [['count', 'Count rows'], ['sum', 'Sum of…'], ['avg', 'Average of…'], ['min', 'Lowest of…'], ['max', 'Highest of…']],
        f.agg, (v) => { f.agg = v as Formula['agg']; render() })))
      if (f.agg !== 'count') form.append(row('Column', fieldControl(f.field, (v) => { f.field = v; applySmartDefaults(); render() })))

      const filterWrap = el('div', 'flex items-center gap-2 flex-1')
      filterWrap.append(checkbox(f.filterOn, (v) => { f.filterOn = v; render() }))
      if (f.filterOn) {
        filterWrap.append(fieldControl(f.filterCol, (v) => { f.filterCol = v; render() }))
        filterWrap.append(el('span', 'text-[11px] text-slate-500', '='))
        const values = [...new Set(rows.map((r) => String(r[f.filterCol] ?? '')))].slice(0, 25)
        if (values.length && values.length <= 25 && headers.length) {
          filterWrap.append(select(values.map((v) => [v, v]), f.filterVal || values[0], (v) => { f.filterVal = v; updatePreview() }))
          if (!f.filterVal) f.filterVal = values[0]
        } else {
          const i = el('input', 'prop-input flex-1') as HTMLInputElement
          i.type = 'text'; i.value = f.filterVal
          i.addEventListener('input', () => { f.filterVal = i.value; updatePreview() })
          filterWrap.append(i)
        }
      } else {
        filterWrap.append(el('span', 'text-[11px] text-slate-500', 'only rows matching a value'))
      }
      form.append(row('Filter', filterWrap))
    } else {
      form.append(row('Column', fieldControl(f.field, (v) => { f.field = v; applySmartDefaults(); render() })))
    }

    const divWrap = el('div', 'flex items-center gap-2 flex-1')
    divWrap.append(checkbox(f.div100, (v) => { f.div100 = v; updatePreview() }))
    divWrap.append(el('span', 'text-[11px] text-slate-500', '÷ 100 — value is stored in cents'))
    form.append(row('Convert', divWrap))

    form.append(row('Format', select(
      [['none', 'As is'], ['number', 'Number'], ['currency', 'Currency'], ['percent', 'Percent'], ['date', 'Date']],
      f.format, (v) => { f.format = v as Formula['format']; render() })))

    if (f.format === 'number' || f.format === 'percent') {
      const d = el('input', 'prop-input') as HTMLInputElement
      d.type = 'number'; d.min = '0'; d.max = '6'; d.value = String(f.decimals); d.style.width = '70px'
      d.addEventListener('change', () => { f.decimals = Math.max(0, Math.min(6, parseInt(d.value, 10) || 0)); updatePreview() })
      form.append(row('Decimals', d))
    } else if (f.format === 'currency') {
      const cur = select([['EUR', '€ EUR'], ['USD', '$ USD'], ['GBP', '£ GBP'], ['CHF', 'CHF'], ['SEK', 'SEK']], f.currency, (v) => { f.currency = v; updatePreview() })
      const loc = select([['nl-NL', '1.234,56 (NL)'], ['de-DE', '1.234,56 (DE)'], ['en-US', '1,234.56 (US)'], ['en-GB', '1,234.56 (UK)'], ['fr-FR', '1 234,56 (FR)']],
        f.locale.startsWith('nl') ? 'nl-NL' : f.locale, (v) => { f.locale = v; updatePreview() })
      const wrap = el('div', 'flex gap-2 flex-1'); wrap.append(cur, loc)
      form.append(row('Currency', wrap))
      f.locale = (loc as HTMLSelectElement).value
    } else if (f.format === 'date') {
      form.append(row('Style', select(DATE_FORMATS, f.dateFmt, (v) => { f.dateFmt = v; updatePreview() })))
    }

    updatePreview()
  }

  /** Sensible automatics when a column is picked: cents → ÷100 + currency; dates → date format. */
  const applySmartDefaults = (): void => {
    if (/cent/i.test(f.field)) { f.div100 = true; f.format = 'currency' }
    else if (/date|_at$|time/i.test(f.field) && f.mode === 'field') { f.format = 'date'; f.div100 = false }
  }

  const body = el('div', 'flex flex-col gap-2')
  body.append(el('p', 'text-xs text-slate-400 -mt-1',
    'Build a computed value without writing code — the expression below stays editable in the cell afterwards.'))
  body.append(form, preview)

  const actions: HTMLElement[] = []
  if (insert) {
    const ins = el('button', 'tb-btn primary', 'Insert into selected cell') as HTMLButtonElement
    ins.addEventListener('click', () => {
      if (insert(buildFormula(f))) toast('Inserted')
      else toast('Click a cell in the table first')
    })
    actions.push(ins)
  }
  const copy = el('button', 'tb-btn', 'Copy') as HTMLButtonElement
  copy.style.cssText = 'background:#334155'
  copy.addEventListener('click', () => { void navigator.clipboard?.writeText(buildFormula(f)); toast('Copied') })
  actions.push(copy)

  openModal({ title: 'Σ Formula builder', body, width: '560px', testid: 'formula-builder', actions })
  render()
}
